import { useState, useEffect, useCallback } from "react";
import {
  CreditCard,
  Check,
  Loader2,
  Crown,
  Zap,
  Shield,
  X,
  ExternalLink,
  Receipt,
  Download,
  RefreshCw,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { useSubscription } from "../context/SubscriptionContext";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLAN_FEATURES = [
  "Scanner illimité de produits",
  "Alertes Telegram & Email en temps réel",
  "Watchlist avec suivi automatique",
  "Export CSV/Excel illimité",
  "Accès à toutes les marketplaces",
  "Mises à jour quotidiennes des opportunités",
  "Support prioritaire",
];

function getUrlParameter(name) {
  const regex = new RegExp("[\\?&]" + name + "=([^&#]*)");
  const results = regex.exec(window.location.search);
  return results === null ? "" : decodeURIComponent(results[1].replace(/\+/g, " "));
}

export default function Subscription() {
  const { email: ctxEmail, isSubscribed: ctxIsSubscribed, refresh: refreshSub } = useSubscription();
  const [email, setEmail] = useState(ctxEmail || "");
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [cancelling, setCancelling] = useState(false);
  const [checkedEmail, setCheckedEmail] = useState(ctxEmail || "");
  const [billingPeriod, setBillingPeriod] = useState("monthly");

  // On mount, if context has email, check subscription
  useEffect(() => {
    if (ctxEmail) {
      checkSubscription(ctxEmail);
    }
  }, [ctxEmail]);

  // If context says subscribed, sync local state
  useEffect(() => {
    if (ctxIsSubscribed && ctxEmail && !subscription) {
      checkSubscription(ctxEmail);
    }
  }, [ctxIsSubscribed]);

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 8;
    const pollInterval = 2500;

    if (attempts >= maxAttempts) {
      setCheckingStatus(false);
      toast.info("Vérification en cours... Rechargez la page dans quelques instants.");
      return;
    }

    try {
      const response = await axios.get(`${API}/subscription/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === "paid") {
        setCheckingStatus(false);
        setPaymentSuccess(true);
        toast.success("Paiement réussi ! Bienvenue chez Profimy Pro !");
        const subEmail = data.metadata?.email || ctxEmail;
        if (subEmail) {
          checkSubscription(subEmail);
          refreshSub();
        }
        return;
      } else if (data.status === "expired") {
        setCheckingStatus(false);
        toast.error("Session de paiement expirée. Veuillez réessayer.");
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch {
      setCheckingStatus(false);
      toast.error("Erreur lors de la vérification du paiement.");
    }
  }, []);

  useEffect(() => {
    const sessionId = getUrlParameter("session_id");
    if (sessionId) {
      setCheckingStatus(true);
      pollPaymentStatus(sessionId);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [pollPaymentStatus]);

  const checkSubscription = async (emailToCheck) => {
    if (!emailToCheck) return;
    try {
      const res = await axios.get(`${API}/subscription/check`, { params: { email: emailToCheck } });
      setSubscription(res.data.subscription || null);
      setCheckedEmail(emailToCheck);
      // Also fetch payment history
      const histRes = await axios.get(`${API}/subscription/history`, { params: { email: emailToCheck } });
      setTransactions(histRes.data.transactions || []);
    } catch {
      setSubscription(null);
    }
  };

  const handleSubscribe = async () => {
    if (!email) {
      toast.error("Veuillez entrer votre email");
      return;
    }
    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const endpoint = billingPeriod === "annual"
        ? `${API}/subscription/checkout/annual`
        : `${API}/subscription/checkout`;
      const response = await axios.post(endpoint, {
        origin_url: originUrl,
        email,
      });
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error("Erreur lors de la creation du paiement");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!checkedEmail) return;
    setCancelling(true);
    try {
      await axios.post(`${API}/subscription/cancel`, null, { params: { email: checkedEmail } });
      toast.success("Abonnement annulé (prendra effet a la fin de la période en cours)");
      checkSubscription(checkedEmail);
    } catch {
      toast.error("Erreur lors de l'annulation");
    } finally {
      setCancelling(false);
    }
  };

  const handleReactivate = async () => {
    if (!checkedEmail) return;
    setCancelling(true);
    try {
      await axios.post(`${API}/subscription/reactivate`, null, { params: { email: checkedEmail } });
      toast.success("Abonnement reactiv\u00e9 avec succ\u00e8s !");
      checkSubscription(checkedEmail);
    } catch {
      toast.error("Erreur lors de la r\u00e9activation");
    } finally {
      setCancelling(false);
    }
  };

  // Checking payment status after Stripe redirect
  if (checkingStatus) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-6" data-testid="payment-checking">
        <Loader2 className="animate-spin text-[#00E676]" size={64} />
        <p className="text-xl font-bold">Vérification du paiement en cours...</p>
        <p className="text-muted-foreground">Veuillez patienter quelques instants</p>
      </div>
    );
  }

  // Payment success screen
  if (paymentSuccess) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-6" data-testid="payment-success">
        <div className="bg-[#00E676] p-6 border-4 border-[#0A0A0A]">
          <Check className="text-[#0A0A0A]" size={64} />
        </div>
        <h1 className="text-4xl font-extrabold">Bienvenue !</h1>
        <p className="text-muted-foreground text-center max-w-md">
          Votre abonnement Profimy Pro est maintenant actif. Profitez de toutes les fonctionnalités !
        </p>
        <button
          onClick={() => setPaymentSuccess(false)}
          className="neo-button-primary"
        >
          Gérer mon abonnement
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="subscription-page">
      {/* Header */}
      <div>
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight flex items-center gap-3">
          <Crown className="text-[#00E676]" size={40} />
          ABONNEMENT
        </h1>
        <p className="text-muted-foreground mt-2 uppercase tracking-wider text-sm">
          Débloquez tout le potentiel de Profimy Margin
        </p>
      </div>

      {/* Pricing Card */}
      <div className="max-w-lg mx-auto">
        {/* Toggle monthly/annual */}
        <div className="flex items-center justify-center gap-4 mb-6" data-testid="billing-toggle">
          <button
            onClick={() => setBillingPeriod("monthly")}
            className={`px-5 py-2 font-bold text-sm uppercase tracking-wider border-2 transition-colors ${
              billingPeriod === "monthly"
                ? "bg-[#00E676] text-[#0A0A0A] border-[#0A0A0A]"
                : "bg-transparent text-muted-foreground border-[#333] hover:border-[#00E676]"
            }`}
          >
            Mensuel
          </button>
          <button
            onClick={() => setBillingPeriod("annual")}
            className={`px-5 py-2 font-bold text-sm uppercase tracking-wider border-2 transition-colors relative ${
              billingPeriod === "annual"
                ? "bg-[#00E676] text-[#0A0A0A] border-[#0A0A0A]"
                : "bg-transparent text-muted-foreground border-[#333] hover:border-[#00E676]"
            }`}
          >
            Annuel
            <span className="absolute -top-3 -right-3 bg-[#FF6B35] text-white text-[10px] px-2 py-0.5 font-bold">
              -14%
            </span>
          </button>
        </div>

        <div className="neo-card p-8 border-[#00E676] relative overflow-hidden" data-testid="pricing-card">
          <div className="absolute top-0 right-0 bg-[#00E676] text-[#0A0A0A] px-4 py-1 font-bold text-sm border-b-2 border-l-2 border-[#0A0A0A]">
            {billingPeriod === "annual" ? "MEILLEURE OFFRE" : "POPULAIRE"}
          </div>
          
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold mb-2">Profimy Pro</h2>
            {billingPeriod === "monthly" ? (
              <>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-6xl font-extrabold">19</span>
                  <span className="text-2xl font-bold">€</span>
                  <span className="text-muted-foreground text-lg">/mois</span>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  Sans engagement — Annulable a tout moment
                </p>
              </>
            ) : (
              <>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-6xl font-extrabold">197</span>
                  <span className="text-2xl font-bold">€</span>
                  <span className="text-muted-foreground text-lg">/an</span>
                </div>
                <div className="mt-2 space-y-1">
                  <p className="text-sm font-bold text-[#00E676]">
                    Economisez 31€ par rapport au mensuel (228€/an)
                  </p>
                  <p className="text-xs text-muted-foreground line-through">
                    19€ x 12 mois = 228€
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Soit 16,42€/mois — Annulable a tout moment
                  </p>
                </div>
              </>
            )}
          </div>

          <div className="space-y-3 mb-8">
            {PLAN_FEATURES.map((feature, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <div className="bg-[#00E676] p-0.5 flex-shrink-0">
                  <Check size={14} className="text-[#0A0A0A]" />
                </div>
                <span className="text-sm font-medium">{feature}</span>
              </div>
            ))}
          </div>

          {!subscription ? (
            <div className="space-y-4">
              <Input
                type="email"
                placeholder="votre@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="neo-input w-full"
                data-testid="subscription-email-input"
              />
              <button
                onClick={handleSubscribe}
                disabled={loading || !email}
                className="neo-button-primary w-full flex items-center justify-center gap-2 text-lg py-4"
                data-testid="subscribe-btn"
              >
                {loading ? (
                  <Loader2 className="animate-spin" size={22} />
                ) : (
                  <Zap size={22} />
                )}
                {billingPeriod === "annual" ? "S'abonner — 197€/an" : "S'abonner — 19€/mois"}
              </button>
              <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                <Shield size={14} />
                Paiement securise par Stripe
              </div>
            </div>
          ) : (
            <div className="bg-[#00E676]/10 border-2 border-[#00E676] p-4 text-center">
              <Check className="mx-auto text-[#00E676] mb-2" size={32} />
              <p className="font-bold">Abonnement actif</p>
              <p className="text-sm text-muted-foreground">{checkedEmail}</p>
            </div>
          )}
        </div>
      </div>

      {/* Manage Subscription */}
      <div className="neo-card p-6" data-testid="manage-subscription">
        <h2 className="text-base md:text-lg font-bold uppercase tracking-tight mb-6 flex items-center gap-2">
          <CreditCard size={22} />
          Gérer mon abonnement
        </h2>
        
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <Input
            type="email"
            placeholder="Entrez votre email pour vérifier"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="neo-input flex-1"
            data-testid="check-email-input"
          />
          <button
            onClick={() => checkSubscription(email)}
            className="neo-button-secondary flex items-center gap-2"
            data-testid="check-subscription-btn"
          >
            Vérifier
          </button>
        </div>

        {checkedEmail && (
          <div className="space-y-4">
            {subscription ? (
              <div className="border-2 border-[#00E676] p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Crown className="text-[#00E676]" size={20} />
                    <span className="font-bold">Profimy Pro — Actif</span>
                  </div>
                  <span className="text-[#00E676] font-bold">
                    {subscription.plan === "annual_197" ? "197€/an" : "19€/mois"}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mb-4">
                  Activé le {new Date(subscription.activated_at).toLocaleDateString("fr-FR")}
                </p>
                <button
                  onClick={handleCancel}
                  disabled={cancelling}
                  className="flex items-center gap-2 text-sm px-4 py-2 border-2 border-[#FF3B30] text-[#FF3B30] font-bold uppercase tracking-wider hover:bg-[#FF3B30] hover:text-white transition-colors"
                  data-testid="cancel-subscription-btn"
                >
                  {cancelling ? <Loader2 className="animate-spin" size={14} /> : <X size={14} />}
                  Annuler l'abonnement
                </button>
              </div>
            ) : subscription?.status === "cancelled" ? (
              <div className="border-2 border-[#FFCC00] p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-[#FFCC00] rounded-full" />
                    <span className="font-bold">Profimy Pro — Annule</span>
                  </div>
                  <span className="text-[#FFCC00] font-bold text-xs">Annule le {new Date(subscription.cancelled_at).toLocaleDateString("fr-FR")}</span>
                </div>
                <p className="text-xs text-muted-foreground mb-4">
                  Votre abonnement a ete annule. Vous pouvez le reactiver a tout moment.
                </p>
                <button
                  onClick={handleReactivate}
                  disabled={cancelling}
                  className="flex items-center gap-2 text-sm px-4 py-2 border-2 border-[#00E676] text-[#00E676] font-bold uppercase tracking-wider hover:bg-[#00E676] hover:text-[#0A0A0A] transition-colors"
                  data-testid="reactivate-subscription-btn"
                >
                  {cancelling ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />}
                  Reactiver l'abonnement
                </button>
              </div>
            ) : (
              <div className="border-2 border-[#0A0A0A] dark:border-[#00E676]/30 p-4 text-center">
                <p className="text-muted-foreground">Aucun abonnement actif pour {checkedEmail}</p>
              </div>
            )}

            {/* Payment History */}
            {transactions.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold uppercase tracking-wider text-sm flex items-center gap-2">
                    <Receipt size={16} />
                    Historique des paiements
                  </h3>
                  <a
                    href={`${API}/subscription/invoices/csv?email=${encodeURIComponent(checkedEmail)}`}
                    download
                    className="neo-button-secondary text-xs px-3 py-1.5 flex items-center gap-1.5"
                    data-testid="export-invoices-csv-btn"
                  >
                    <Download size={14} />
                    Export CSV
                  </a>
                </div>
                <div className="space-y-2">
                  {transactions.map((tx, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 border-2 border-[#0A0A0A] dark:border-[#00E676]/30 text-sm">
                      <div>
                        <span className="font-bold">19,00€</span>
                        <span className="text-muted-foreground ml-2">
                          {new Date(tx.created_at).toLocaleDateString("fr-FR")}
                        </span>
                      </div>
                      <span className={`px-2 py-0.5 text-xs font-bold uppercase ${
                        tx.payment_status === "paid" 
                          ? "bg-[#00E676] text-[#0A0A0A]" 
                          : tx.payment_status === "pending"
                          ? "bg-[#FFCC00] text-[#0A0A0A]"
                          : "bg-[#FF3B30] text-white"
                      }`}>
                        {tx.payment_status === "paid" ? "Payé" : tx.payment_status === "pending" ? "En attente" : tx.payment_status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
